<template>
    <Dialog :visible="showDialog" class="action-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header-danger">
                Reactivate a License
            </div>
            <div class="dialog-content align-items-center">
                <div class="w-full flex justify-content-center align-items-center mb-3">
                    <SvgWarning className="popup-warning-svg" color="var(--popup-warning-color)"/>
                </div>
                <span class="text mb-3 px-1 flex flex-column align-items-center text-center gap-1">
                    <span>
                        It seems that you have reformatted your hard drive, started from a clone, or performed another operation
                        <span style="font-weight: var(--font-weight-normal);">that has invalidated your activation</span>.
                        Make sure to do a
                        <span style="font-weight: var(--font-weight-normal); color: var(--mydanger-color);">clean “Release”</span>
                        of your activated licenses
                        <span style="font-weight: var(--font-weight-normal); color: var(--mydanger-color);">before doing any of these operations</span>.
                        So next time, make sure to check our help center (click on the “?” below) for instructions on when and how to do a clean "Release". 
                    </span>
                    <span>
                        Please be aware that frequent use of this operation may lead to your account being flagged for review.
                    </span>
                </span>

                <div class="content flex flex-column w-full justify-content-left mt-2">
                    <DataTable :value="[license]" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-2" style="max-height: 25vh;">
                        <Column field="serial_number" header="Serial number">
                            <template #body="slotProps">
                                <span class="text-serial-number">{{ slotProps.data.serial_number }}</span>
                            </template>
                        </Column>
                        <Column field="product.application" header="Application" style="max-width: 30%">
                            <template #body="slotProps">
                                <span>{{ isWebView && applicationGlobalInfo.name ?  applicationGlobalInfo.name : slotProps.data.product.application ?? "/" }}{{ slotProps.data.current_status.application_instance_id && slotProps.data.current_status.application_instance_id>0 ? ` ${slotProps.data.current_status.application_instance_id}` : "" }}</span>
                            </template>
                        </Column>
                        <Column header="Device">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.current_status.activeHardware">{{ slotProps.data.current_status.activeHardware.label ?? "/" }}</span>
                                <span v-else class="font-italic">/</span>
                            </template>
                        </Column>
                        <Column header="User">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.current_status.activeUser">{{ slotProps.data.current_status.activeUser.first_name ?? "" }} {{ slotProps.data.current_status.activeUser.last_name ?? "" }}</span>
                                <span v-else class="font-italic">/</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div class="mt-2 flex flex-column text">
                    <span class="text-center px-2 sm:px-7">Are you sure you want to reactivate the license above ?</span>
                </div>
            </div>

            <div class="dialog-footer">
                <a  class="justify-self-start flex align-items-center justify-content-center" :href="forceReleaseZendeskLink" target="_blank" @click="clickedZendeskLink">
                    <Button rounded text style="border-radius: 980px; padding: 0;">
                        <SvgQuestionMarkCircle className="info-svg" color="var(--button-normal-color)"/>
                    </Button>
                </a>
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="$emit('close')"/>
                    <ButtonModule type="primary" severity="danger" label="Reactivate" @click="$emit('reactivate', license.serial_number)"/>
                </div>
            </div>
        </template>
    </Dialog>

</template>

<script setup>
// --- IMPORT ---
import { computed } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
// vue
import ButtonModule from '@/components/Modules/ButtonModule.vue'
import SvgWarning from '@/components/SVGs/SvgWarning.vue';
import SvgQuestionMarkCircle from '@/components/SVGs/SvgQuestionMarkCircle.vue';
// Others
import { useStore } from 'vuex';

const store = useStore()

// --- isWebView info ---
const isWebView = computed(() => store.state.applicationWebView.isWebView)
const applicationGlobalInfo = computed(() => store.state.applicationWebView.applicationGlobalInfo)

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    license: Object,
})

// --- EMITS ---
const emit = defineEmits(['close', 'reactivate'])

// --- Support link ---
const forceReleaseZendeskLink = "https://softron.zendesk.com/hc/en-us/sections/14666349935516-Softron-License-Manager-New-2024-Activations"
const clickedZendeskLink = () => {
    // (in case it is in the web view in the app, ask the app to open it also, because the href thing will not work if it is in the web view)
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
        window.webkit.messageHandlers.jsOpenWebPage.postMessage(forceReleaseZendeskLink);
    }
}
</script>

<style scoped>
</style>